import {useCallback, useEffect, useState} from 'react';
import type {EmblaCarouselType} from 'embla-carousel';

import {usePrefersReducedMotion} from '@/modules/foundation/hooks/usePrefersReducedMotion';

type UsePrevNextButtonsType = {
	prevBtnDisabled: boolean;
	nextBtnDisabled: boolean;
	onPrevButtonClick: () => void;
	onNextButtonClick: () => void;
};

export const usePrevNextButtons = (
	emblaApi: EmblaCarouselType | undefined,
): UsePrevNextButtonsType => {
	const prefersReducedMotion = usePrefersReducedMotion();
	const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
	const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

	const onPrevButtonClick = useCallback(() => {
		if (!emblaApi) {
			return;
		}
		emblaApi.scrollPrev(prefersReducedMotion);
	}, [emblaApi, prefersReducedMotion]);

	const onNextButtonClick = useCallback(() => {
		if (!emblaApi) {
			return;
		}
		emblaApi.scrollNext(prefersReducedMotion);
	}, [emblaApi, prefersReducedMotion]);

	const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
		setPrevBtnDisabled(!emblaApi.canScrollPrev());
		setNextBtnDisabled(!emblaApi.canScrollNext());
	}, []);

	useEffect(() => {
		if (!emblaApi) {
			return;
		}

		// NOTE: Using the setTimeout here feels wrong, but since we're not allowed to call
		//       setState from a useEffect anymore (maybe we never were?) we have to delay the
		//       initialization until after the useEffect has run. Ideally we would use the
		//       "init" event from embla, but that may already have fired before the emblaApi is
		//       available here.
		const timeout = setTimeout(() => onSelect(emblaApi), 0);
		const handler = emblaApi.on('reInit', onSelect).on('select', onSelect);
		return () => {
			clearTimeout(timeout);
			handler.clear();
		};
	}, [emblaApi, onSelect]);

	return {
		prevBtnDisabled,
		nextBtnDisabled,
		onPrevButtonClick,
		onNextButtonClick,
	};
};
